"use client";

import { type SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { Icon } from "@iconify-icon/react";

import { sendPersonalData } from "@/features/auth/api/onboarding";
import useAuthStore from "@/features/auth/store/useAuthStore";
import errorHandler, { assignApiErrorsToFormFields } from "@/features/auth/utils/errorHandler";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DatePicker from "@/components/DatePicker";
import useUserStore from "@/features/auth/store/useUserStore.ts";

interface PersonalDataFormProps {
  goToNextStep: () => void;
  goToPrevStep: () => void;
}

const PersonalDataForm = ({ goToNextStep, goToPrevStep }: PersonalDataFormProps) => {
  const setOnboardingData = useAuthStore((state) => state.setOnboardingData);
  const { t } = useTranslation(["forms", "common"]);
  const { user } = useUserStore();
  const {
    onboardingData: { firstName, lastName, birthDate, accountType },
  } = useAuthStore();

  const personalDataFormSchema = useMemo(
    () =>
      z.object({
        firstName: z.string().min(1, t("forms:errors.firstNameReq")),
        lastName: z.string().min(1, t("forms:errors.lastNameReq")),
        birthDate: z
          .string()
          .refine((date) => date, { message: t("forms:errors.dobReq") })
          .refine(
            (date) => {
              if (!date) return false;

              const parsedBirthDate = new Date(date);
              const today = new Date();
              const userRole = accountType;

              // Tutors must be at least 18
              if (userRole === "tutor") {
                const minAge = 18;
                const maxAllowedBirthDate = new Date(today);

                maxAllowedBirthDate.setFullYear(today.getFullYear() - minAge);

                return parsedBirthDate <= maxAllowedBirthDate;
              }

              // Students: no age limit (still prevent future dates via DatePicker)
              return true;
            },
            {
              message: t("forms:errors.tutorAgeReq"),
            },
          ),
      }),
    [t, accountType],
  );

  type PersonalDataFormValues = z.infer<typeof personalDataFormSchema>;
  const form = useForm<PersonalDataFormValues>({
    resolver: zodResolver(personalDataFormSchema),
    mode: "onTouched",
    defaultValues: {
      firstName: user?.firstName ?? firstName ?? "",
      lastName: user?.lastName ?? lastName ?? "",
      birthDate: birthDate ?? "",
    },
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    setError,
  } = form;

  const onSubmit: SubmitHandler<PersonalDataFormValues> = async ({
    firstName,
    lastName,
    birthDate,
  }) => {
    console.log(form.getValues());
    try {
      const res = await sendPersonalData({
        firstName,
        lastName,
        birthDate,
      });

      setOnboardingData({ firstName, lastName, birthDate });

      console.log(res);
      goToNextStep();
    } catch (error) {
      const apiErrors = errorHandler<"first_name" | "last_name" | "date_of_birth">(error);

      assignApiErrorsToFormFields(apiErrors, setError, {
        firstName: "first_name",
        lastName: "last_name",
        birthDate: "date_of_birth",
      });
    }
  };

  return (
    <Form {...form}>
      <form className="grid gap-12" onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 gap-4 gap-y-6 sm:grid-cols-2">
          <FormField
            control={control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("forms:labels.firstName", "First Name")}
                  <span className="text-destructive ml-1">*</span>
                </FormLabel>
                <FormControl>
                  <Input className="bg-side-accent text-sm" disabled={isSubmitting} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("forms:labels.lastName", "Last Name")}
                  <span className="text-destructive ml-1">*</span>
                </FormLabel>
                <FormControl>
                  <Input className="bg-side-accent text-sm" disabled={isSubmitting} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="birthDate"
            render={({ field }) => {
              return (
                <FormItem className="flex flex-col sm:col-span-2">
                  <FormLabel>
                    {t("forms:labels.dateOfBirth", "Date of birth")}
                    <span className="text-destructive ml-1">*</span>
                  </FormLabel>
                  <DatePicker
                    maxDate={(() => {
                      const today = new Date();
                      const userRole = accountType;

                      if (userRole === "tutor") {
                        // Tutors must be at least 18 years old
                        const maxDate = new Date(today);

                        maxDate.setFullYear(today.getFullYear() - 18);

                        return maxDate;
                      }

                      // Students: allow any past date up to today
                      return today;
                    })()}
                    selected={field.value ? new Date(field.value) : undefined}
                    onSelect={(val) => {
                      field.onChange(val ? format(val, "yyyy-MM-dd") : undefined);
                    }}
                  />
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>
        <div className={"relative z-50 flex w-full gap-2"}>
          <Button
            className={
              "bg-muted/20 hover:bg-muted/30 border-border/50 w-20 shadow-lg backdrop-blur-sm"
            }
            disabled={isSubmitting}
            size="lg"
            type="button"
            variant={"outline"}
            onClick={goToPrevStep}
          >
            <Icon icon={"mdi:arrow-left"} />
            {t("common:back")}
          </Button>
          <Button
            className={"w-full"}
            disabled={isSubmitting || !form.formState.isValid}
            size="lg"
            type="submit"
            variant={"foreground"}
          >
            {t("common:continue")}
            <Icon icon={"mdi:arrow-right"} />
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default PersonalDataForm;
